import { Toggle, type toggleVariants } from "@timeghost/ui/shadcn/toggle";
import type { VariantProps } from "class-variance-authority";
import { AnimatePresence, motion } from "motion/react";
import { type ComponentProps, type JSX, memo, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type ToggleSize = NonNullable<VariantProps<typeof toggleVariants>["size"]>;
type ToggleVariant = NonNullable<VariantProps<typeof toggleVariants>["variant"]>;

export interface IconToggleButtonProps extends Omit<ComponentProps<typeof Toggle>, "children" | "size" | "variant"> {
	readonly children: ReactNode;
	/** When set, icon swaps animate with AnimatePresence. */
	readonly motionKey?: string;
	readonly size?: ToggleSize | "icon-sm";
	readonly variant?: ToggleVariant | "ghost" | "brand";
}

const resolveSize = (size: IconToggleButtonProps["size"]): ToggleSize => {
	if (size === "icon-sm") {
		return "sm";
	}

	return size ?? "default";
};

function IconToggleButton({
	className,
	variant = "default",
	size = "default",
	pressed = false,
	onPressedChange,
	motionKey,
	children,
	...props
}: IconToggleButtonProps): JSX.Element {
	const toggleVariant: ToggleVariant = variant === "outline" ? "outline" : variant === "brand" ? "brand" : "default";

	return (
		<Toggle
			className={cn(
				className,
				"text-muted-foreground transition-colors duration-100",
				size === "icon-sm" && "size-7 min-w-7 px-0",
				variant === "ghost" && "hover:bg-accent data-[state=on]:bg-transparent data-[state=on]:text-foreground",
				variant === "default" && "hover:bg-accent data-[state=on]:bg-transparent data-[state=on]:text-primary"
			)}
			data-slot="icon-toggle-button"
			data-state={pressed ? "on" : "off"}
			onPressedChange={onPressedChange}
			pressed={pressed}
			size={resolveSize(size)}
			variant={toggleVariant}
			{...props}>
			{motionKey ? (
				<span className="relative flex size-4 items-center justify-center">
					<AnimatePresence initial={false} mode="wait">
						<motion.span
							animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
							className="absolute inset-0 flex items-center justify-center"
							exit={{ opacity: 0, y: 8, rotate: 12, scale: 0.8 }}
							initial={{ opacity: 0, y: -8, rotate: -12, scale: 0.8 }}
							key={motionKey}
							transition={{ duration: 0.2, ease: "easeOut" }}>
							{children}
						</motion.span>
					</AnimatePresence>
				</span>
			) : (
				children
			)}
		</Toggle>
	);
}

export default memo(IconToggleButton);
